import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import axios from "axios";
import {
  Settings,
  Shield,
  Building2,
  Palette,
  Plus,
  Edit,
  Trash2,
  Save,
  Check,
  X,
  MessageCircle,
  QrCode,
  Clock,
  Info,
  Wifi,
  WifiOff,
  RefreshCw,
  LogOut,
  CheckCircle,
  CheckCircle2,
  Loader2,
  Upload,
  Image as ImageIcon,
  Heart,
  FileText,
  MapPin,
  Video,
  Link,
  Bell,
  Cake,
  Play as PlayIcon,
  Calendar,
  Users,
  Zap,
  GripVertical,
  Home,
  Newspaper,
  ClipboardList,
  BookOpen,
  Church,
  Music,
  Footprints,
  DollarSign,
  Briefcase,
  Globe,
  Grid3X3,
  ArrowUp,
  ArrowDown,
  ExternalLink
} from "lucide-react";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import QRCode from "react-qr-code";
import { availableShortcuts, defaultBottomNavConfig } from "../components/MobileLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultPermissions = {
  dashboard: { ver: true, criar: false, editar: false, excluir: false },
  pessoas: { ver: true, criar: false, editar: false, excluir: false },
  ministerios: { ver: true, criar: false, editar: false, excluir: false },
  funcoes: { ver: true, criar: false, editar: false, excluir: false },
  musicas: { ver: true, criar: false, editar: false, excluir: false },
  eventos: { ver: true, criar: false, editar: false, excluir: false },
  escalas: { ver: true, criar: false, editar: false, excluir: false },
  social: { ver: false, criar: false, editar: false, excluir: false },
  whatsapp: { ver: false, criar: false, editar: false, excluir: false },
  trilho_reino: { ver: false, criar: false, editar: false, excluir: false },
  cadastro: { ver: false, criar: false, editar: false, excluir: false },
  financeiro: { ver: false, criar: false, editar: false, excluir: false },
  configuracoes: { ver: false, criar: false, editar: false, excluir: false },
  formularios: { ver: false, criar: false, editar: false, excluir: false },
  usuarios: { ver: false, criar: false, editar: false, excluir: false },
  news_config: { ver: false, criar: false, editar: false, excluir: false },
  agenda_config: { ver: false, criar: false, editar: false, excluir: false },
  dashboard_cards: {
    proximas_escalas: true,
    proximos_eventos: true,
    estatisticas: true,
    acoes_rapidas: true
  }
};

const moduleLabels = {
  dashboard: "Dashboard",
  pessoas: "Pessoas",
  ministerios: "Ministérios",
  funcoes: "Funções",
  musicas: "Músicas",
  eventos: "Eventos",
  escalas: "Escalas",
  social: "Inscrição / Social",
  whatsapp: "WhatsApp",
  trilho_reino: "Trilho do Reino",
  cadastro: "Cadastro Público",
  financeiro: "Financeiro",
  configuracoes: "Configurações",
  formularios: "Configuração de Formulários",
  usuarios: "Usuários",
  news_config: "Config. News",
  agenda_config: "Agenda / Programação"
};

// Módulos que são sub-itens de Configurações
const configSubModules = ["formularios", "news_config", "agenda_config"];
const mainModules = Object.keys(moduleLabels).filter(m => !configSubModules.includes(m));

const dashboardCardLabels = {
  proximas_escalas: "Próximas Escalas",
  proximos_eventos: "Próximos Eventos",
  estatisticas: "Estatísticas",
  acoes_rapidas: "Ações Rápidas"
};

const actionLabels = {
  ver: "Ver",
  criar: "Criar",
  editar: "Editar",
  excluir: "Excluir"
};

export default function SettingsPage() {
  const navigate = useNavigate();
  const { currentBranch, user } = useAuth();
  const [activeTab, setActiveTab] = useState("profiles");
  const [loading, setLoading] = useState(true);
  
  // Selected branch for appearance settings (can differ from currentBranch)
  const [settingsBranchId, setSettingsBranchId] = useState(null);
  
  // Access Profiles
  const [profiles, setProfiles] = useState([]);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [profileForm, setProfileForm] = useState({
    nome: "",
    descricao: "",
    permissoes: { ...defaultPermissions },
    criar_usuario_pessoa: false,
    aprovar_membro: false,
    aprovar_funcao: false,
    perfil_voluntario_aprovado: false,
    ver_todas_escalas: false,
    ver_todas_pessoas: false,
    ver_todos_ministerios: false,
    ver_todos_projetos: false,
    receber_notificacoes_whatsapp: false,
    atalho: ""  // Atalho na barra de navegação inferior
  });
  
  // Branches
  const [branches, setBranches] = useState([]);
  const [branchDialogOpen, setBranchDialogOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [branchForm, setBranchForm] = useState({
    nome: "",
    tipo: "igreja",
    endereco: "",
    telefone: "",
    email: "",
    ativa: true,
    logo_url: "",
    cor_primaria: "#2563eb",
    sobre_nos: "",
    latitude: "",
    longitude: "",
    raio_checkin_metros: 100,
    tempo_minimo_checkin_minutos: 20,
    permite_checkin_gps: true,
    permite_checkin_wifi: true,
    termos_uso: `Ao utilizar este aplicativo, você concorda com os seguintes termos:

1. AUTORIZAÇÃO DE IMAGEM: Autorizo o uso de minha imagem, captada em fotos ou vídeos durante eventos e atividades realizadas nas dependências da unidade, para fins de divulgação institucional.

2. PERMISSÃO DE LOCALIZAÇÃO: Autorizo o uso da minha localização para registro automático de presença nos cultos e eventos. Sua localização será verificada apenas quando você estiver nas proximidades da unidade e por tempo suficiente para confirmar sua participação.

3. PRIVACIDADE: Seus dados serão tratados de forma confidencial e utilizados apenas para os fins descritos acima, em conformidade com a LGPD.`
  });
  
  // System Settings
  const [systemSettings, setSystemSettings] = useState({
    logo_url: "",
    cor_primaria: "#2563eb",
    cor_secundaria: "#1e40af",
    nomenclaturas: {
      pessoas: "Pessoas",
      ministerio: "Ministério",
      funcao: "Função"
    }
  });

  // Bottom Navigation Config
  const [bottomNavConfig, setBottomNavConfig] = useState([]);
  const [showAddNavItem, setShowAddNavItem] = useState(false);
  const [newNavItem, setNewNavItem] = useState({ value: "", label: "", path: "", icon: "Home", isExternal: false });

  // WhatsApp Settings
  const [whatsappSettings, setWhatsappSettings] = useState({
    enabled: false,
    tempo_antes_evento: "2",
    unidade_tempo: "horas",
    mensagem_escala: `Olá *{nome}*! 👋

Você foi escalado(a) para servir:

📅 *Evento:* {evento}
🗓️ *Data:* {data}
⏰ *Horário:* {horario}
📍 *Local:* {local}
🎯 *Função:* {funcao}
⏰ *Chegada:* {hora_voluntario}

🎵 *Músicas:*
{musicas}

━━━━━━━━━━━━━━━
✅ *CONFIRMAR PRESENÇA*
👉 {link_confirmacao}

📱 *VER MINHA ESCALA*
👉 {link}
━━━━━━━━━━━━━━━

Deus te abençoe! 🙏`,
    mensagem_lembrete: `⏰ *LEMBRETE DE ESCALA* ⏰

Olá *{nome}*!

Não esqueça, você está escalado(a) para *HOJE*!

📅 *Data:* {data}
⏰ *Horário:* {horario}
📍 *Local:* {local}
🎯 *Função:* {funcao}

━━━━━━━━━━━━━━━
📱 *ACESSAR ESCALA*
👉 {link}
━━━━━━━━━━━━━━━

Te esperamos! 🙌`,
    mensagem_aprovacao: `Olá {nome}! 🎉

Parabéns! Você foi aprovado(a) para servir conosco!

📋 Ministério(s): {ministerios}
🎯 Função: {funcao}

Seja bem-vindo(a) à equipe! Em breve entraremos em contato com mais informações.

Deus abençoe!`,
    mensagem_cadastro: `Olá {nome}! 🙏

Seu cadastro foi realizado com sucesso!

Seus dados de acesso:
📧 Usuário: {email}
🔐 Senha: {senha}

Acesse o sistema e altere sua senha se desejar.

Deus abençoe!`,
    // Configurações de notificação de alteração
    notificar_alteracao_escala: false,
    notificar_alteracao_evento: true,
    notificar_alteracao_horario: true,
    notificar_alteracao_local: true,
    notificar_alteracao_musicas: true,
    notificar_alteracao_observacoes: false,
    mensagem_alteracao: `⚠️ *ALTERAÇÃO NA ESCALA* ⚠️

Olá *{nome}*!

Houve uma alteração na escala em que você está escalado(a):

📅 *Evento:* {evento}
🗓️ *Data:* {data}
⏰ *Horário:* {horario}
📍 *Local:* {local}

🔄 *O que foi alterado:*
{alteracoes}

━━━━━━━━━━━━━━━
📱 *VER ESCALA ATUALIZADA*
👉 {link}
━━━━━━━━━━━━━━━

Obrigado pela compreensão! 🙏`
  });

  // WhatsApp Connection State
  const [whatsappStatus, setWhatsappStatus] = useState({
    connected: false,
    status: "disconnected",
    user: null,
    qr: null
  });
  const [loadingWhatsapp, setLoadingWhatsapp] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // Banner Settings
  const [dashboardBanner, setDashboardBanner] = useState({
    tipo: "image",
    url: "",
    titulo: ""
  });
  const [savingBanner, setSavingBanner] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);

  // Birthday/Reminder Settings
  const [birthdaySettings, setBirthdaySettings] = useState({
    enabled: true,
    mensagem: "🎂 Feliz Aniversário, {nome}! 🎉\n\nQue Deus abençoe grandemente este novo ano de vida!\n\nCom carinho,\nEquipe 8 Life",
    notificar_pessoa: true,
    notificar_lideres: true,
    horario_envio: "08:00"
  });
  const [savingBirthday, setSavingBirthday] = useState(false);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [runningManual, setRunningManual] = useState(false);

  // Form Configuration (Portal Cativo + Cadastro Público)
  const [formConfig, setFormConfig] = useState({
    // Configurações visuais
    cor_primaria: "#f97316",
    cor_secundaria: "#ea580c",
    cor_texto: "#1f2937",
    cor_fundo: "#ffffff",
    logo_url: "",
    titulo_portal_wifi: "WiFi Life Canedo",
    subtitulo_portal_wifi: "Conecte-se para acessar a internet",
    titulo_cadastro_publico: "Cadastre-se",
    subtitulo_cadastro_publico: "Faça parte da nossa comunidade",
    // Campos disponíveis
    campos: {
      nome_completo: { visivel: true, obrigatorio: true, label: "Nome completo" },
      telefone: { visivel: true, obrigatorio: true, label: "Telefone" },
      email: { visivel: true, obrigatorio: false, label: "Email" },
      data_nascimento: { visivel: true, obrigatorio: false, label: "Data de nascimento" },
      sexo: { visivel: true, obrigatorio: false, label: "Sexo" },
      estado_civil: { visivel: false, obrigatorio: false, label: "Estado civil" },
      cidade: { visivel: false, obrigatorio: false, label: "Cidade" },
      endereco: { visivel: false, obrigatorio: false, label: "Endereço" },
      bairro: { visivel: false, obrigatorio: false, label: "Bairro" },
      profissao: { visivel: false, obrigatorio: false, label: "Profissão" },
      como_conheceu: { visivel: false, obrigatorio: false, label: "Como nos conheceu" },
      batizado: { visivel: false, obrigatorio: false, label: "É batizado?" },
      membro_outra_igreja: { visivel: false, obrigatorio: false, label: "Membro de outra igreja?" },
      ministerios_interesse: { visivel: true, obrigatorio: false, label: "Ministérios de interesse" }
    },
    // Configurações específicas por tipo de formulário
    portal_wifi: {
      mostrar_cadastro: true,
      mostrar_login: true,
      campos_login: ["telefone"],
      campos_cadastro: ["nome_completo", "telefone", "email"]
    },
    cadastro_publico: {
      campos_visiveis: ["nome_completo", "telefone", "email", "data_nascimento", "sexo", "ministerios_interesse"]
    }
  });
  const [savingFormConfig, setSavingFormConfig] = useState(false);

  // Omada Controller Configuration
  const [omadaConfig, setOmadaConfig] = useState({
    url: "",
    operator_username: "",
    operator_password: "",
    client_id: "",
    client_secret: "",
    controller_id: "",
    site_id: "",
    ativo: true
  });
  const [savingOmada, setSavingOmada] = useState(false);
  const [testingOmada, setTestingOmada] = useState(false);
  const [omadaTestResult, setOmadaTestResult] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  // Load settings when settingsBranchId changes
  useEffect(() => {
    if (settingsBranchId !== null) {
      loadBranchSettings(settingsBranchId);
    }
  }, [settingsBranchId]);

  // Set initial branch when currentBranch or branches load
  useEffect(() => {
    if (currentBranch && settingsBranchId === null) {
      setSettingsBranchId(currentBranch.id);
    }
  }, [currentBranch, settingsBranchId]);

  // Load form config when forms tab is selected
  useEffect(() => {
    if (activeTab === "forms") {
      loadFormConfig();
    }
  }, [activeTab]);

  const loadData = async () => {
    try {
      const [profilesRes, branchesRes, whatsappRes, bannerRes, birthdayRes, schedulerRes] = await Promise.all([
        axios.get(`${API}/access-profiles`),
        axios.get(`${API}/branches`),
        axios.get(`${API}/settings/whatsapp`).catch(() => ({ data: null })),
        axios.get(`${API}/dashboard/banner`).catch(() => ({ data: null })),
        axios.get(`${API}/settings/birthday-notifications`).catch(() => ({ data: null })),
        axios.get(`${API}/scheduler/status`).catch(() => ({ data: null }))
      ]);
      setProfiles(profilesRes.data);
      setBranches(branchesRes.data);
      if (whatsappRes.data) {
        setWhatsappSettings(prev => ({ ...prev, ...whatsappRes.data }));
      }
      if (bannerRes.data) {
        setDashboardBanner(bannerRes.data);
      }
      if (birthdayRes.data) {
        setBirthdaySettings(prev => ({ ...prev, ...birthdayRes.data }));
      }
      if (schedulerRes.data) {
        setSchedulerStatus(schedulerRes.data);
      }
      
      // Load system settings for current branch
      if (currentBranch) {
        const settingsRes = await axios.get(`${API}/settings/system`, {
          params: { filial_id: currentBranch.id }
        });
        setSystemSettings(settingsRes.data);
        setSettingsBranchId(currentBranch.id);
      } else {
        const settingsRes = await axios.get(`${API}/settings/system`);
        setSystemSettings(settingsRes.data);
      }
    } catch (error) {
      toast.error("Erro ao carregar configurações");
    } finally {
      setLoading(false);
    }
  };

  const loadBranchSettings = async (branchId) => {
    try {
      const settingsRes = await axios.get(`${API}/settings/system`, {
        params: { filial_id: branchId }
      });
      setSystemSettings(settingsRes.data);
      
      // Load branch data for bottom_nav_config
      const branchRes = await axios.get(`${API}/branches`);
      const branch = branchRes.data?.find(b => b.id === branchId);
      if (branch?.bottom_nav_config && Array.isArray(branch.bottom_nav_config)) {
        setBottomNavConfig(branch.bottom_nav_config);
      } else {
        // Set default config
        setBottomNavConfig(defaultBottomNavConfig);
      }
    } catch (error) {
      console.error("Error loading branch settings:", error);
    }
  };

  // ===== FORM CONFIG FUNCTIONS =====
  const loadFormConfig = async () => {
    try {
      const res = await axios.get(`${API}/settings/form-config`);
      if (res.data) {
        setFormConfig(prev => ({ ...prev, ...res.data }));
      }
    } catch (error) {
      console.error("Error loading form config:", error);
    }
  };

  const saveFormConfig = async () => {
    setSavingFormConfig(true);
    try {
      await axios.post(`${API}/settings/form-config`, formConfig);
      toast.success("Configurações de formulário salvas!");
    } catch (error) {
      toast.error("Erro ao salvar configurações");
    } finally {
      setSavingFormConfig(false);
    }
  };

  // ===== OMADA CONFIG FUNCTIONS =====
  const loadOmadaConfig = async () => {
    try {
      // Get all configs and find the one for current branch
      const res = await axios.get(`${API}/wifi/portal/configs`);
      const configs = res.data || [];
      const filialId = currentBranch?.id || "default";
      const config = configs.find(c => c.filial_id === filialId) || configs[0];
      
      if (config) {
        setOmadaConfig({
          url: config.omada_controller_url || "",
          operator_username: config.omada_operator_user || "",
          operator_password: "", // Don't load password for security
          client_id: config.omada_client_id || "",
          client_secret: "", // Don't load secret for security
          controller_id: config.omada_controller_id || "",
          site_id: config.omada_site_id || "Default",
          ativo: config.ativo !== false
        });
      }
    } catch (error) {
      console.log("No Omada config found");
    }
  };

  const saveOmadaConfig = async () => {
    if (!omadaConfig.url) {
      toast.error("Informe a URL do Omada Controller");
      return;
    }
    if (!omadaConfig.operator_username || !omadaConfig.operator_password) {
      toast.error("Informe o usuário e senha do operador");
      return;
    }

    setSavingOmada(true);
    try {
      const filialId = currentBranch?.id || "default";
      await axios.post(`${API}/wifi/portal/configs`, {
        filial_id: filialId,
        omada_controller_url: omadaConfig.url,
        omada_operator_user: omadaConfig.operator_username,
        omada_operator_password: omadaConfig.operator_password,
        omada_client_id: omadaConfig.client_id || null,
        omada_client_secret: omadaConfig.client_secret || null,
        omada_controller_id: omadaConfig.controller_id || null,
        omada_site_id: omadaConfig.site_id || "Default",
        ativo: true
      });
      toast.success("Configuração do Omada salva com sucesso!");
      setOmadaTestResult(null);
    } catch (error) {
      // If config already exists, try to update
      if (error.response?.status === 400 && error.response?.data?.detail?.includes("já existe")) {
        try {
          // Get existing config ID
          const existingRes = await axios.get(`${API}/wifi/portal/configs`);
          const existingConfig = existingRes.data.find(c => c.filial_id === (currentBranch?.id || "default"));
          if (existingConfig) {
            await axios.put(`${API}/wifi/portal/configs/${existingConfig.id}`, {
              filial_id: currentBranch?.id || "default",
              omada_controller_url: omadaConfig.url,
              omada_operator_user: omadaConfig.operator_username,
              omada_operator_password: omadaConfig.operator_password,
              omada_client_id: omadaConfig.client_id || null,
              omada_client_secret: omadaConfig.client_secret || null,
              omada_controller_id: omadaConfig.controller_id || null,
              omada_site_id: omadaConfig.site_id || "Default",
              ativo: true
            });
            toast.success("Configuração do Omada atualizada!");
            setOmadaTestResult(null);
            return;
          }
        } catch (updateError) {
          const msg = updateError.response?.data?.detail || "Erro ao atualizar configuração";
          toast.error(msg);
        }
      } else {
        const msg = error.response?.data?.detail || "Erro ao salvar configuração";
        toast.error(msg);
      }
    } finally {
      setSavingOmada(false);
    }
  };

  const testOmadaConnection = async () => {
    if (!omadaConfig.url) {
      toast.error("Informe a URL do Omada Controller");
      return;
    }
    if (!omadaConfig.operator_username || !omadaConfig.operator_password) {
      toast.error("Informe o usuário e senha do operador");
      return;
    }

    setTestingOmada(true);
    setOmadaTestResult(null);
    try {
      const res = await axios.post(`${API}/wifi/portal/test-omada`, {
        omada_url: omadaConfig.url,
        operator_username: omadaConfig.operator_username,
        operator_password: omadaConfig.operator_password
      });
      setOmadaTestResult({
        success: res.data.success,
        message: res.data.message || "Conexão bem sucedida!"
      });
      if (res.data.success) {
        toast.success("Conexão com Omada estabelecida!");
      } else {
        toast.error(res.data.message || "Falha na conexão");
      }
    } catch (error) {
      const msg = error.response?.data?.detail || "Erro ao testar conexão";
      setOmadaTestResult({
        success: false,
        message: msg
      });
      toast.error(msg);
    } finally {
      setTestingOmada(false);
    }
  };

  // Load Omada config when wifi tab is selected
  useEffect(() => {
    if (activeTab === "wifi") {
      loadOmadaConfig();
    }
  }, [activeTab, currentBranch]);

  const toggleCampoVisivel = (campo) => {
    setFormConfig(prev => ({
      ...prev,
      campos: {
        ...prev.campos,
        [campo]: {
          ...prev.campos[campo],
          visivel: !prev.campos[campo].visivel
        }
      }
    }));
  };

  const toggleCampoObrigatorio = (campo) => {
    setFormConfig(prev => ({
      ...prev,
      campos: {
        ...prev.campos,
        [campo]: {
          ...prev.campos[campo],
          obrigatorio: !prev.campos[campo].obrigatorio
        }
      }
    }));
  };

  const updateCampoLabel = (campo, label) => {
    setFormConfig(prev => ({
      ...prev,
      campos: {
        ...prev.campos,
        [campo]: {
          ...prev.campos[campo],
          label
        }
      }
    }));
  };

  // ===== PROFILE FUNCTIONS =====
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        nome: profileForm.nome,
        descricao: profileForm.descricao,
        permissoes: {
          ...profileForm.permissoes,
          criar_usuario_pessoa: profileForm.criar_usuario_pessoa,
          aprovar_membro: profileForm.aprovar_membro,
          aprovar_funcao: profileForm.aprovar_funcao,
          perfil_voluntario_aprovado: profileForm.perfil_voluntario_aprovado,
          ver_todas_escalas: profileForm.ver_todas_escalas,
          ver_todas_pessoas: profileForm.ver_todas_pessoas,
          ver_todos_ministerios: profileForm.ver_todos_ministerios,
          ver_todos_projetos: profileForm.ver_todos_projetos,
          receber_notificacoes_whatsapp: profileForm.receber_notificacoes_whatsapp,
          atalho: profileForm.atalho || null  // Atalho na barra de navegação
        }
      };
      
      if (editingProfile) {
        await axios.put(`${API}/access-profiles/${editingProfile.id}`, payload);
        toast.success("Perfil atualizado!");
      } else {
        await axios.post(`${API}/access-profiles`, payload);
        toast.success("Perfil criado!");
      }
      setProfileDialogOpen(false);
      resetProfileForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar perfil");
    }
  };

  const handleEditProfile = (profile) => {
    setEditingProfile(profile);
    setProfileForm({
      nome: profile.nome,
      descricao: profile.descricao || "",
      permissoes: profile.permissoes || defaultPermissions,
      criar_usuario_pessoa: profile.permissoes?.criar_usuario_pessoa || false,
      aprovar_membro: profile.permissoes?.aprovar_membro || false,
      aprovar_funcao: profile.permissoes?.aprovar_funcao || false,
      perfil_voluntario_aprovado: profile.permissoes?.perfil_voluntario_aprovado || false,
      ver_todas_escalas: profile.permissoes?.ver_todas_escalas || false,
      ver_todas_pessoas: profile.permissoes?.ver_todas_pessoas || false,
      ver_todos_ministerios: profile.permissoes?.ver_todos_ministerios || false,
      ver_todos_projetos: profile.permissoes?.ver_todos_projetos || false,
      receber_notificacoes_whatsapp: profile.permissoes?.receber_notificacoes_whatsapp || false,
      atalho: profile.permissoes?.atalho || ""
    });
    setProfileDialogOpen(true);
  };

  const handleDeleteProfile = async (id) => {
    if (!window.confirm("Deseja remover este perfil?")) return;
    try {
      await axios.delete(`${API}/access-profiles/${id}`);
      toast.success("Perfil removido");
      loadData();
    } catch (error) {
      toast.error("Erro ao remover perfil");
    }
  };

  const resetProfileForm = () => {
    setEditingProfile(null);
    setProfileForm({
      nome: "",
      descricao: "",
      permissoes: { ...defaultPermissions },
      criar_usuario_pessoa: false,
      aprovar_membro: false,
      aprovar_funcao: false,
      perfil_voluntario_aprovado: false,
      ver_todas_escalas: false,
      ver_todas_pessoas: false,
      ver_todos_ministerios: false,
      ver_todos_projetos: false,
      receber_notificacoes_whatsapp: false,
      atalho: ""
    });
  };

  const togglePermission = (module, action) => {
    setProfileForm(prev => {
      const currentModulePerms = prev.permissoes?.[module] || { ver: false, criar: false, editar: false, excluir: false };
      const currentValue = currentModulePerms[action] || false;
      return {
        ...prev,
        permissoes: {
          ...prev.permissoes,
          [module]: {
            ...currentModulePerms,
            [action]: !currentValue
          }
        }
      };
    });
  };

  // ===== BRANCH FUNCTIONS =====
  const handleBranchSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBranch) {
        await axios.put(`${API}/branches/${editingBranch.id}`, branchForm);
        toast.success("Unidade atualizada!");
      } else {
        await axios.post(`${API}/branches`, branchForm);
        toast.success("Unidade criada!");
      }
      setBranchDialogOpen(false);
      resetBranchForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar filial");
    }
  };

  const handleEditBranch = (branch) => {
    setEditingBranch(branch);
    setBranchForm({
      nome: branch.nome,
      tipo: branch.tipo || "igreja",
      endereco: branch.endereco || "",
      telefone: branch.telefone || "",
      email: branch.email || "",
      ativa: branch.ativa,
      logo_url: branch.logo_url || "",
      cor_primaria: branch.cor_primaria || "#2563eb",
      sobre_nos: branch.sobre_nos || "",
      latitude: branch.latitude || "",
      longitude: branch.longitude || "",
      raio_checkin_metros: branch.raio_checkin_metros || 100,
      tempo_minimo_checkin_minutos: branch.tempo_minimo_checkin_minutos || 20,
      permite_checkin_gps: branch.permite_checkin_gps !== false,
      permite_checkin_wifi: branch.permite_checkin_wifi !== false,
      termos_uso: branch.termos_uso || ""
    });
    setBranchDialogOpen(true);
  };

  const handleDeleteBranch = async (id) => {
    if (!window.confirm("Deseja remover esta filial?")) return;
    try {
      await axios.delete(`${API}/branches/${id}`);
      toast.success("Unidade removida");
      loadData();
    } catch (error) {
      toast.error("Erro ao remover filial");
    }
  };

  const resetBranchForm = () => {
    setEditingBranch(null);
    setBranchForm({
      nome: "",
      tipo: "igreja",
      endereco: "",
      telefone: "",
      email: "",
      ativa: true,
      logo_url: "",
      cor_primaria: "#2563eb",
      sobre_nos: "",
      latitude: "",
      longitude: "",
      raio_checkin_metros: 100,
      tempo_minimo_checkin_minutos: 20,
      permite_checkin_gps: true,
      permite_checkin_wifi: true,
      termos_uso: ""
    });
  };

  // ===== SYSTEM SETTINGS FUNCTIONS =====
  const handleSystemSettingsSave = async () => {
    try {
      await axios.put(`${API}/settings/system`, {
        ...systemSettings,
        filial_id: settingsBranchId
      });
      toast.success("Configurações salvas!");
    } catch (error) {
      toast.error("Erro ao salvar configurações");
    }
  };

  // ===== BOTTOM NAV CONFIG FUNCTIONS =====
  const handleSaveBottomNavConfig = async () => {
    try {
      // Get current branch data
      const branchRes = await axios.get(`${API}/branches`);
      const branch = branchRes.data?.find(b => b.id === settingsBranchId);
      
      if (!branch) {
        toast.error("Unidade não encontrada");
        return;
      }

      // Update branch with new bottom_nav_config
      await axios.put(`${API}/branches/${settingsBranchId}`, {
        ...branch,
        bottom_nav_config: bottomNavConfig
      });
      
      // Reload branches in context
      window.location.reload();
      toast.success("Menu de navegação salvo!");
    } catch (error) {
      toast.error("Erro ao salvar menu de navegação");
      console.error(error);
    }
  };

  const handleMoveNavItem = (index, direction) => {
    const newConfig = [...bottomNavConfig];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newConfig.length) return;
    
    // Don't allow moving the center menu item
    if (newConfig[index].isMenu || newConfig[targetIndex].isMenu) {
      toast.error("O menu central não pode ser movido");
      return;
    }
    
    [newConfig[index], newConfig[targetIndex]] = [newConfig[targetIndex], newConfig[index]];
    setBottomNavConfig(newConfig);
  };

  const handleRemoveNavItem = (index) => {
    const item = bottomNavConfig[index];
    if (item.isMenu) {
      toast.error("O menu central não pode ser removido");
      return;
    }
    setBottomNavConfig(bottomNavConfig.filter((_, i) => i !== index));
  };

  const handleAddNavItem = () => {
    if (!newNavItem.label || !newNavItem.path) {
      toast.error("Preencha o nome e o caminho");
      return;
    }
    
    // Ensure we don't exceed 5 items
    if (bottomNavConfig.length >= 5) {
      toast.error("Máximo de 5 itens na navegação");
      return;
    }

    const item = {
      value: newNavItem.value || newNavItem.label.toLowerCase().replace(/\s+/g, '_'),
      label: newNavItem.label,
      path: newNavItem.isExternal ? newNavItem.path : (newNavItem.path.startsWith('/') ? newNavItem.path : `/${newNavItem.path}`),
      icon: newNavItem.icon,
      isExternal: newNavItem.isExternal
    };

    // Find the position before the menu item (center)
    const menuIndex = bottomNavConfig.findIndex(item => item.isMenu);
    const insertIndex = menuIndex >= 0 ? menuIndex : bottomNavConfig.length;
    
    const newConfig = [...bottomNavConfig];
    newConfig.splice(insertIndex, 0, item);
    setBottomNavConfig(newConfig);
    
    setNewNavItem({ value: "", label: "", path: "", icon: "Home", isExternal: false });
    setShowAddNavItem(false);
    toast.success("Item adicionado");
  };

  const handleResetNavConfig = () => {
    setBottomNavConfig(defaultBottomNavConfig);
    toast.success("Menu restaurado para o padrão");
  };

  // ===== BANNER FUNCTIONS =====
  const handleSaveDashboardBanner = async () => {
    setSavingBanner(true);
    try {
      await axios.put(`${API}/dashboard/banner`, dashboardBanner);
      toast.success("Banner do Dashboard salvo!");
    } catch (error) {
      toast.error("Erro ao salvar banner");
    } finally {
      setSavingBanner(false);
    }
  };

  // ===== BIRTHDAY/REMINDER FUNCTIONS =====
  const handleSaveBirthdaySettings = async () => {
    setSavingBirthday(true);
    try {
      await axios.put(`${API}/settings/birthday-notifications`, birthdaySettings);
      toast.success("Configurações de aniversário salvas!");
    } catch (error) {
      toast.error("Erro ao salvar configurações");
    } finally {
      setSavingBirthday(false);
    }
  };

  const handleRunReminders = async () => {
    setRunningManual(true);
    try {
      await axios.post(`${API}/scheduler/run-reminders`);
      toast.success("Verificação de lembretes executada!");
    } catch (error) {
      toast.error("Erro ao executar verificação");
    } finally {
      setRunningManual(false);
    }
  };

  const handleRunBirthdays = async () => {
    setRunningManual(true);
    try {
      await axios.post(`${API}/scheduler/run-birthdays`);
      toast.success("Verificação de aniversários executada!");
    } catch (error) {
      toast.error("Erro ao executar verificação");
    } finally {
      setRunningManual(false);
    }
  };

  const handleBannerImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Arquivo muito grande. Máximo 5MB.");
      return;
    }
    
    setUploadingBanner(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const res = await axios.post(`${API}/dashboard/banner/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setDashboardBanner(prev => ({ ...prev, tipo: "image", url: res.data.url }));
      toast.success("Imagem enviada!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao enviar imagem");
    } finally {
      setUploadingBanner(false);
      e.target.value = "";
    }
  };

  // Logo upload function
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Arquivo muito grande. Máximo 5MB.");
      return;
    }
    
    setUploadingLogo(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const url = settingsBranchId 
        ? `${API}/settings/upload-logo?filial_id=${settingsBranchId}`
        : `${API}/settings/upload-logo`;
      const response = await axios.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setSystemSettings(prev => ({ ...prev, logo_url: response.data.logo_url }));
      toast.success("Logo atualizada com sucesso!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao fazer upload da logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!window.confirm("Deseja remover a logo?")) return;
    
    try {
      const url = settingsBranchId 
        ? `${API}/settings/remove-logo?filial_id=${settingsBranchId}`
        : `${API}/settings/remove-logo`;
      await axios.delete(url);
      setSystemSettings(prev => ({ ...prev, logo_url: null }));
      toast.success("Logo removida");
    } catch (error) {
      toast.error("Erro ao remover logo");
    }
  };

  // ===== WHATSAPP SETTINGS FUNCTIONS =====
  const handleWhatsappSettingsSave = async () => {
    try {
      await axios.put(`${API}/settings/whatsapp`, whatsappSettings);
      toast.success("Configurações do WhatsApp salvas!");
    } catch (error) {
      toast.error("Erro ao salvar configurações do WhatsApp");
    }
  };

  // Load WhatsApp connection status
  const loadWhatsappStatus = useCallback(async () => {
    try {
      const [statusRes, qrRes] = await Promise.all([
        axios.get(`${API}/whatsapp/status`),
        axios.get(`${API}/whatsapp/qr`)
      ]);
      setWhatsappStatus({
        connected: statusRes.data.connected,
        status: statusRes.data.status,
        user: statusRes.data.user,
        qr: qrRes.data.qr
      });
    } catch (error) {
      console.log("WhatsApp service unavailable");
      setWhatsappStatus(prev => ({ ...prev, status: "service_unavailable" }));
    }
  }, []);

  // Poll WhatsApp status when on WhatsApp tab
  useEffect(() => {
    if (activeTab === "whatsapp" && whatsappSettings.enabled) {
      loadWhatsappStatus();
      const interval = setInterval(loadWhatsappStatus, 5000);
      return () => clearInterval(interval);
    }
  }, [activeTab, whatsappSettings.enabled, loadWhatsappStatus]);

  const handleWhatsappDisconnect = async () => {
    setLoadingWhatsapp(true);
    try {
      await axios.post(`${API}/whatsapp/disconnect`);
      toast.success("WhatsApp desconectado!");
      setWhatsappStatus({
        connected: false,
        status: "disconnected",
        user: null,
        qr: null
      });
      setTimeout(loadWhatsappStatus, 2000);
    } catch (error) {
      toast.error("Erro ao desconectar WhatsApp");
    } finally {
      setLoadingWhatsapp(false);
    }
  };

  const handleWhatsappRefresh = () => {
    loadWhatsappStatus();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="settings-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-xl bg-primary/10">
          <Settings className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Configurações</h1>
          <p className="text-sm text-muted-foreground">Gerencie perfis, filiais e aparência</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-7 mb-6">
          <TabsTrigger value="profiles" className="gap-2" data-testid="tab-profiles">
            <Shield className="h-4 w-4" />
            <span className="hidden sm:inline">Perfis</span>
          </TabsTrigger>
          <TabsTrigger value="branches" className="gap-2" data-testid="tab-branches">
            <Building2 className="h-4 w-4" />
            <span className="hidden sm:inline">Unidades</span>
          </TabsTrigger>
          <TabsTrigger value="banners" className="gap-2" data-testid="tab-banners">
            <ImageIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Banners</span>
          </TabsTrigger>
          <TabsTrigger value="reminders" className="gap-2" data-testid="tab-reminders">
            <Bell className="h-4 w-4" />
            <span className="hidden sm:inline">Agendador</span>
          </TabsTrigger>
          <TabsTrigger value="appearance" className="gap-2" data-testid="tab-appearance">
            <Palette className="h-4 w-4" />
            <span className="hidden sm:inline">Aparência</span>
          </TabsTrigger>
          <TabsTrigger value="wifi" className="gap-2" data-testid="tab-wifi">
            <Wifi className="h-4 w-4" />
            <span className="hidden sm:inline">WiFi</span>
          </TabsTrigger>
          {(user?.is_admin || user?.tipo === "admin" || user?.nivel_acesso === "admin") && (
            <TabsTrigger value="tools" className="gap-2" data-testid="tab-tools">
              <Settings className="h-4 w-4" />
              <span className="hidden sm:inline">Ferramentas</span>
            </TabsTrigger>
          )}
          {(user?.permissoes?.formularios?.ver || user?.permissoes?.configuracoes?.ver || user?.is_admin) && (
            <TabsTrigger value="forms" className="gap-2" data-testid="tab-forms">
              <FileText className="h-4 w-4" />
              <span className="hidden sm:inline">Formulários</span>
            </TabsTrigger>
          )}
        </TabsList>

        {/* ===== PROFILES TAB ===== */}
        <TabsContent value="profiles" className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Perfis de Acesso</h2>
              <p className="text-sm text-muted-foreground">Defina permissões por perfil</p>
            </div>
            <Button onClick={() => { resetProfileForm(); setProfileDialogOpen(true); }} data-testid="add-profile-button">
              <Plus className="h-4 w-4 mr-2" />
              Novo Perfil
            </Button>
          </div>

          {/* Link to Public Form Config */}
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <FileText className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium">Configuração do Formulário Público</h3>
                    <p className="text-sm text-muted-foreground">Configure campos e vincule a uma unidade</p>
                  </div>
                </div>
                <Button variant="outline" onClick={() => navigate("/formulario-config")} data-testid="go-form-config">
                  Configurar
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-3">
            {profiles.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Shield className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">Nenhum perfil cadastrado</p>
                </CardContent>
              </Card>
            ) : (
              profiles.map((profile) => (
                <Card key={profile.id} data-testid={`profile-card-${profile.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold">{profile.nome}</h3>
                          {profile.permissoes?.perfil_voluntario_aprovado && (
                            <Badge className="bg-green-500 text-white text-xs">Voluntário Aprovado</Badge>
                          )}
                          {profile.permissoes?.ver_todas_escalas && (
                            <Badge className="bg-blue-500 text-white text-xs">Ver Todas Escalas</Badge>
                          )}
                        </div>
                        {profile.descricao && (
                          <p className="text-sm text-muted-foreground">{profile.descricao}</p>
                        )}
                        <div className="flex flex-wrap gap-1 mt-2">
                          {Object.entries(profile.permissoes || {}).map(([module, perms]) => {
                            if (module === "criar_usuario_pessoa") return null;
                            if (!perms?.ver) return null;
                            return (
                              <Badge key={module} variant="secondary" className="text-xs">
                                {moduleLabels[module]}
                              </Badge>
                            );
                          })}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleEditProfile(profile)}
                          data-testid={`edit-profile-${profile.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-destructive"
                          onClick={() => handleDeleteProfile(profile.id)}
                          data-testid={`delete-profile-${profile.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* ===== BRANCHES TAB ===== */}
        <TabsContent value="branches" className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Unidades</h2>
              <p className="text-sm text-muted-foreground">Gerencie suas unidades</p>
            </div>
            <Button onClick={() => { resetBranchForm(); setBranchDialogOpen(true); }} data-testid="add-branch-button">
              <Plus className="h-4 w-4 mr-2" />
              Nova Unidade
            </Button>
          </div>

          <div className="space-y-3">
            {branches.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Building2 className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">Nenhuma filial cadastrada</p>
                </CardContent>
              </Card>
            ) : (
              branches.map((branch) => (
                <Card key={branch.id} data-testid={`branch-card-${branch.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{branch.nome}</h3>
                          <Badge variant={branch.ativa ? "default" : "secondary"}>
                            {branch.ativa ? "Ativa" : "Inativa"}
                          </Badge>
                        </div>
                        {branch.endereco && (
                          <p className="text-sm text-muted-foreground mt-1">{branch.endereco}</p>
                        )}
                        {(branch.telefone || branch.email) && (
                          <p className="text-sm text-muted-foreground">
                            {[branch.telefone, branch.email].filter(Boolean).join(" • ")}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleEditBranch(branch)}
                          data-testid={`edit-branch-${branch.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-destructive"
                          onClick={() => handleDeleteBranch(branch.id)}
                          data-testid={`delete-branch-${branch.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* ===== BANNERS TAB ===== */}
        <TabsContent value="banners" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ImageIcon className="h-5 w-5 text-blue-500" />
                Banner da Página Inicial (Dashboard)
              </CardTitle>
              <CardDescription>Configure o banner que aparece no topo do Dashboard para usuários logados</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Banner Type */}
              <div className="space-y-2">
                <Label>Tipo de Banner</Label>
                <Select 
                  value={dashboardBanner.tipo || "image"} 
                  onValueChange={(v) => setDashboardBanner(prev => ({ ...prev, tipo: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="image">
                      <div className="flex items-center gap-2">
                        <ImageIcon className="h-4 w-4" />
                        Imagem
                      </div>
                    </SelectItem>
                    <SelectItem value="video">
                      <div className="flex items-center gap-2">
                        <Video className="h-4 w-4" />
                        Vídeo (YouTube)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Image Banner */}
              {dashboardBanner.tipo === "image" && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Imagem do Banner</Label>
                    {dashboardBanner.url ? (
                      <div className="relative rounded-lg overflow-hidden border-2 border-dashed border-primary/40 bg-muted/30">
                        <div className="aspect-[3/1]">
                          <img 
                            src={dashboardBanner.url} 
                            alt="Preview do banner" 
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                          <div className="flex justify-end gap-2">
                            <div className="relative">
                              <input
                                type="file"
                                accept="image/*"
                                className="absolute inset-0 opacity-0 cursor-pointer"
                                onChange={handleBannerImageUpload}
                                disabled={uploadingBanner}
                              />
                              <Button variant="secondary" size="sm" disabled={uploadingBanner}>
                                {uploadingBanner ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Upload className="h-4 w-4" />
                                )}
                                <span className="ml-2">Trocar</span>
                              </Button>
                            </div>
                            <Button 
                              variant="destructive" 
                              size="sm"
                              onClick={() => setDashboardBanner(prev => ({ ...prev, url: "" }))}
                            >
                              <Trash2 className="h-4 w-4" />
                              <span className="ml-2">Remover</span>
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="relative rounded-lg border-2 border-dashed border-muted-foreground/25 hover:border-primary/50 transition-colors">
                        <input
                          type="file"
                          accept="image/*"
                          className="absolute inset-0 opacity-0 cursor-pointer"
                          onChange={handleBannerImageUpload}
                          disabled={uploadingBanner}
                        />
                        <div className="aspect-[3/1] flex flex-col items-center justify-center gap-2 p-8">
                          {uploadingBanner ? (
                            <>
                              <Loader2 className="h-10 w-10 text-muted-foreground animate-spin" />
                              <p className="text-sm text-muted-foreground">Enviando...</p>
                            </>
                          ) : (
                            <>
                              <Upload className="h-10 w-10 text-muted-foreground" />
                              <p className="text-sm text-muted-foreground text-center">
                                Clique ou arraste uma imagem aqui<br />
                                <span className="text-xs">1200 x 400 pixels (proporção 3:1) • JPG, PNG ou WebP • Máx 5MB</span>
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* URL input as fallback */}
                    <div className="flex gap-2">
                      <Input
                        value={dashboardBanner.url || ""}
                        onChange={(e) => setDashboardBanner(prev => ({ ...prev, url: e.target.value }))}
                        placeholder="Ou cole uma URL de imagem aqui"
                        className="text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Video Banner */}
              {dashboardBanner.tipo === "video" && (
                <div className="space-y-2">
                  <Label>URL do Vídeo (YouTube)</Label>
                  <Input
                    value={dashboardBanner.url || ""}
                    onChange={(e) => setDashboardBanner(prev => ({ ...prev, url: e.target.value }))}
                    placeholder="https://youtube.com/watch?v=..."
                  />
                </div>
              )}

              {/* Title */}
              <div className="space-y-2">
                <Label>Título do Banner (opcional)</Label>
                <Input
                  value={dashboardBanner.titulo || ""}
                  onChange={(e) => setDashboardBanner(prev => ({ ...prev, titulo: e.target.value }))}
                  placeholder="Bem-vindo à Igreja"
                />
              </div>

              {/* Size info */}
              <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-blue-900 dark:text-blue-100">Tamanhos Recomendados</p>
                    <ul className="text-blue-700 dark:text-blue-300 mt-1 space-y-1">
                      <li><strong>Banner Dashboard:</strong> 1200 x 400 pixels (proporção 3:1)</li>
                      <li><strong>Formatos:</strong> JPG, PNG ou WebP</li>
                      <li><strong>Tamanho máximo:</strong> 5MB</li>
                    </ul>
                  </div>
                </div>
              </div>

              <Button onClick={handleSaveDashboardBanner} disabled={savingBanner} className="w-full">
                {savingBanner ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Salvar Banner do Dashboard
              </Button>
            </CardContent>
          </Card>

          {/* Link para News Config */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ImageIcon className="h-5 w-5 text-purple-500" />
                Banner da Página News (Pública)
              </CardTitle>
              <CardDescription>Configure o banner da página pública de eventos e links</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 rounded-lg bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-900">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-purple-600 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-purple-900 dark:text-purple-100">Tamanhos Recomendados</p>
                    <ul className="text-purple-700 dark:text-purple-300 mt-1 space-y-1">
                      <li><strong>Banner News:</strong> 1080 x 1080 pixels (proporção 1:1) ou 1200 x 630 (proporção 1.9:1)</li>
                      <li><strong>Formatos:</strong> JPG, PNG ou WebP</li>
                      <li><strong>Tamanho máximo:</strong> 5MB</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <Button 
                variant="outline" 
                className="w-full gap-2"
                onClick={() => window.location.href = "/news-config"}
              >
                <Link className="h-4 w-4" />
                Ir para Configurações do News
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== REMINDERS TAB ===== */}
        <TabsContent value="reminders" className="space-y-4">
          {/* Schedule Reminders Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-500" />
                Status do Agendador
              </CardTitle>
              <CardDescription>
                Monitore o status dos jobs automáticos (lembretes, aniversários, etc)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-blue-900 dark:text-blue-100">Como funciona</p>
                    <ul className="text-blue-700 dark:text-blue-300 mt-1 space-y-1">
                      <li>• O sistema verifica automaticamente a cada 10 minutos</li>
                      <li>• Envia lembretes conforme configurado em <strong>WhatsApp → Configurações</strong></li>
                      <li>• Envia via WhatsApp e Push Notification</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Scheduler Status */}
              {schedulerStatus && (
                <div className={`p-4 rounded-lg border ${schedulerStatus.running ? 'bg-green-50 dark:bg-green-950/30 border-green-200' : 'bg-red-50 dark:bg-red-950/30 border-red-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {schedulerStatus.running ? (
                        <>
                          <CheckCircle className="h-5 w-5 text-green-500" />
                          <span className="font-medium text-green-700 dark:text-green-300">Agendador Ativo</span>
                        </>
                      ) : (
                        <>
                          <X className="h-5 w-5 text-red-500" />
                          <span className="font-medium text-red-700 dark:text-red-300">Agendador Inativo</span>
                        </>
                      )}
                    </div>
                    <Button variant="outline" size="sm" onClick={() => loadData()}>
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Atualizar
                    </Button>
                  </div>
                  {schedulerStatus.jobs?.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {schedulerStatus.jobs.map(job => (
                        <div key={job.id} className="text-sm text-muted-foreground flex items-center gap-2">
                          <Calendar className="h-3 w-3" />
                          <span>{job.name}</span>
                          {job.next_run && (
                            <span className="text-xs">
                              (próxima: {new Date(job.next_run).toLocaleString('pt-BR')})
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <Button 
                onClick={handleRunReminders} 
                disabled={runningManual}
                variant="outline"
                className="w-full"
              >
                {runningManual ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <PlayIcon className="h-4 w-4 mr-2" />
                )}
                Executar Verificação Agora
              </Button>

              {/* Link to WhatsApp Config */}
              <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <MessageCircle className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="font-medium text-green-900 dark:text-green-100">Configurações de Mensagens</p>
                      <p className="text-sm text-green-700 dark:text-green-300">Configure templates, aniversários e conexão WhatsApp</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => window.location.href = '/whatsapp'}>
                    Acessar
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== APPEARANCE TAB ===== */}
        <TabsContent value="appearance" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Aparência e Nomenclaturas</CardTitle>
              <CardDescription>Personalize a aparência e os termos do sistema por unidade</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Branch Selector */}
              <div className="p-4 rounded-lg bg-muted/50 border">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-medium">Unidade</Label>
                    <p className="text-sm text-muted-foreground">
                      Selecione a unidade para configurar a aparência
                    </p>
                  </div>
                  <Select
                    value={settingsBranchId || ""}
                    onValueChange={(val) => setSettingsBranchId(val)}
                  >
                    <SelectTrigger className="w-[220px]" data-testid="appearance-branch-select">
                      <SelectValue placeholder="Selecione a unidade" />
                    </SelectTrigger>
                    <SelectContent>
                      {branches?.map(branch => (
                        <SelectItem key={branch.id} value={branch.id}>
                          <span>{branch.nome || 'Unidade sem nome'}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h3 className="font-medium">Cores do Sistema</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Cor Primária</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={systemSettings.cor_primaria}
                        onChange={(e) => setSystemSettings({ ...systemSettings, cor_primaria: e.target.value })}
                        className="w-12 h-10 p-1 cursor-pointer"
                        data-testid="color-primary"
                      />
                      <Input
                        value={systemSettings.cor_primaria}
                        onChange={(e) => setSystemSettings({ ...systemSettings, cor_primaria: e.target.value })}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Cor Secundária</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={systemSettings.cor_secundaria}
                        onChange={(e) => setSystemSettings({ ...systemSettings, cor_secundaria: e.target.value })}
                        className="w-12 h-10 p-1 cursor-pointer"
                        data-testid="color-secondary"
                      />
                      <Input
                        value={systemSettings.cor_secundaria}
                        onChange={(e) => setSystemSettings({ ...systemSettings, cor_secundaria: e.target.value })}
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h3 className="font-medium">Logo</h3>
                <div className="space-y-4">
                  {/* Preview atual */}
                  {systemSettings.logo_url && (
                    <div className="p-4 rounded-lg bg-muted/50 flex items-center justify-center gap-4">
                      <img 
                        src={systemSettings.logo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${systemSettings.logo_url}` : systemSettings.logo_url} 
                        alt="Logo Preview" 
                        className="max-h-20 object-contain" 
                      />
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-destructive"
                        onClick={handleRemoveLogo}
                        data-testid="remove-logo"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Remover
                      </Button>
                    </div>
                  )}
                  
                  {/* Upload de arquivo */}
                  <div className="space-y-2">
                    <Label>Upload da Logo</Label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Input
                          type="file"
                          accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                          onChange={handleLogoUpload}
                          disabled={uploadingLogo}
                          className="cursor-pointer"
                          data-testid="logo-upload"
                        />
                      </div>
                      {uploadingLogo && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span className="text-sm">Enviando...</span>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Formatos aceitos: PNG, JPG, GIF, WebP, SVG. Tamanho máximo: 5MB
                    </p>
                  </div>
                  
                  {/* Fallback: URL externa (opcional) */}
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Ou use uma URL externa</Label>
                    <Input
                      placeholder="https://exemplo.com/logo.png"
                      value={systemSettings.logo_url?.startsWith('/') ? '' : (systemSettings.logo_url || '')}
                      onChange={(e) => setSystemSettings({ ...systemSettings, logo_url: e.target.value })}
                      data-testid="logo-url"
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* ===== BOTTOM NAVIGATION CONFIG ===== */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium flex items-center gap-2">
                      <Grid3X3 className="h-4 w-4" />
                      Menu de Navegação (Mobile)
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Configure os itens que aparecem na barra inferior do app
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={handleResetNavConfig}>
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Restaurar Padrão
                    </Button>
                  </div>
                </div>

                {/* Current Nav Items */}
                <div className="space-y-2 p-4 bg-muted/50 rounded-lg">
                  <Label className="text-sm text-muted-foreground">Itens atuais (máx. 5)</Label>
                  <div className="space-y-2">
                    {bottomNavConfig.map((item, index) => {
                      const IconComponent = {
                        Home, Newspaper, Church, Users, Briefcase, Music, Calendar, Footprints,
                        MessageCircle, BookOpen, ClipboardList, DollarSign, Settings, Grid3X3, Globe
                      }[item.icon] || Home;
                      
                      return (
                        <div 
                          key={item.value || index}
                          className={`flex items-center gap-3 p-3 bg-white rounded-lg border ${item.isMenu ? 'border-primary bg-primary/5' : ''}`}
                        >
                          <div className="flex items-center gap-2 flex-1">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${item.isMenu ? 'bg-primary text-white' : 'bg-muted'}`}>
                              <IconComponent className="h-4 w-4" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-sm">{item.label}</p>
                              <p className="text-xs text-muted-foreground">
                                {item.isMenu ? 'Menu Central (fixo)' : item.path}
                                {item.isExternal && <span className="ml-1 text-blue-500">(externo)</span>}
                              </p>
                            </div>
                          </div>
                          
                          {!item.isMenu && (
                            <div className="flex items-center gap-1">
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8"
                                onClick={() => handleMoveNavItem(index, 'up')}
                                disabled={index === 0 || bottomNavConfig[index - 1]?.isMenu}
                              >
                                <ArrowUp className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8"
                                onClick={() => handleMoveNavItem(index, 'down')}
                                disabled={index === bottomNavConfig.length - 1 || bottomNavConfig[index + 1]?.isMenu}
                              >
                                <ArrowDown className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8 text-destructive"
                                onClick={() => handleRemoveNavItem(index)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {bottomNavConfig.length < 5 && (
                    <Button 
                      variant="outline" 
                      className="w-full mt-2"
                      onClick={() => setShowAddNavItem(true)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Adicionar Item
                    </Button>
                  )}
                </div>

                {/* Add Item Dialog */}
                {showAddNavItem && (
                  <div className="p-4 border rounded-lg space-y-4 bg-white">
                    <div className="flex items-center justify-between">
                      <Label className="font-medium">Novo Item de Menu</Label>
                      <Button variant="ghost" size="icon" onClick={() => setShowAddNavItem(false)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Nome</Label>
                        <Input
                          placeholder="Ex: Escalas"
                          value={newNavItem.label}
                          onChange={(e) => setNewNavItem({ ...newNavItem, label: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Ícone</Label>
                        <Select 
                          value={newNavItem.icon} 
                          onValueChange={(v) => setNewNavItem({ ...newNavItem, icon: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Home">Casa</SelectItem>
                            <SelectItem value="Newspaper">News</SelectItem>
                            <SelectItem value="BookOpen">Devocional</SelectItem>
                            <SelectItem value="Calendar">Calendário</SelectItem>
                            <SelectItem value="ClipboardList">Escalas</SelectItem>
                            <SelectItem value="Users">Pessoas</SelectItem>
                            <SelectItem value="Church">Igreja</SelectItem>
                            <SelectItem value="Music">Música</SelectItem>
                            <SelectItem value="MessageCircle">WhatsApp</SelectItem>
                            <SelectItem value="Footprints">Trilho</SelectItem>
                            <SelectItem value="DollarSign">Financeiro</SelectItem>
                            <SelectItem value="Briefcase">Projetos</SelectItem>
                            <SelectItem value="Settings">Configurações</SelectItem>
                            <SelectItem value="Globe">Link Externo</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label>Caminho/URL</Label>
                        <div className="flex items-center gap-2 ml-auto">
                          <Label className="text-sm text-muted-foreground">Link externo?</Label>
                          <Switch
                            checked={newNavItem.isExternal}
                            onCheckedChange={(checked) => setNewNavItem({ ...newNavItem, isExternal: checked, path: "" })}
                          />
                        </div>
                      </div>
                      {newNavItem.isExternal ? (
                        <Input
                          placeholder="https://exemplo.com"
                          value={newNavItem.path}
                          onChange={(e) => setNewNavItem({ ...newNavItem, path: e.target.value })}
                        />
                      ) : (
                        <Select 
                          value={newNavItem.path} 
                          onValueChange={(v) => setNewNavItem({ ...newNavItem, path: v })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione uma página" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableShortcuts.map(shortcut => (
                              <SelectItem key={shortcut.value} value={shortcut.path}>
                                {shortcut.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>

                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setShowAddNavItem(false)}>
                        Cancelar
                      </Button>
                      <Button onClick={handleAddNavItem}>
                        <Plus className="h-4 w-4 mr-2" />
                        Adicionar
                      </Button>
                    </div>
                  </div>
                )}

                <Button onClick={handleSaveBottomNavConfig} className="w-full">
                  <Save className="h-4 w-4 mr-2" />
                  Salvar Menu de Navegação
                </Button>
              </div>

              <Separator />

              <div className="space-y-4">
                <h3 className="font-medium">Nomenclaturas</h3>
                <p className="text-sm text-muted-foreground">Personalize os termos usados no sistema</p>
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <Label>Termo para "Pessoas"</Label>
                    <Input
                      value={systemSettings.nomenclaturas?.pessoas || "Pessoas"}
                      onChange={(e) => setSystemSettings({
                        ...systemSettings,
                        nomenclaturas: { ...systemSettings.nomenclaturas, pessoas: e.target.value }
                      })}
                      data-testid="nomenclatura-pessoas"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Termo para "Ministério"</Label>
                    <Input
                      value={systemSettings.nomenclaturas?.ministerio || "Ministério"}
                      onChange={(e) => setSystemSettings({
                        ...systemSettings,
                        nomenclaturas: { ...systemSettings.nomenclaturas, ministerio: e.target.value }
                      })}
                      data-testid="nomenclatura-ministerio"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Termo para "Função"</Label>
                    <Input
                      value={systemSettings.nomenclaturas?.funcao || "Função"}
                      onChange={(e) => setSystemSettings({
                        ...systemSettings,
                        nomenclaturas: { ...systemSettings.nomenclaturas, funcao: e.target.value }
                      })}
                      data-testid="nomenclatura-funcao"
                    />
                  </div>
                </div>
              </div>

              <Button onClick={handleSystemSettingsSave} className="w-full" data-testid="save-settings">
                <Save className="h-4 w-4 mr-2" />
                Salvar Configurações
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== WIFI PORTAL TAB ===== */}
        <TabsContent value="wifi" className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Portal WiFi (Captive Portal)</h2>
              <p className="text-sm text-muted-foreground">Configure o portal de acesso WiFi para visitantes</p>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wifi className="h-5 w-5" />
                Configuração do Portal
              </CardTitle>
              <CardDescription>
                Configure a página de login para visitantes que conectarem no WiFi da igreja
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Info Alert */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-800 flex items-center gap-2">
                  <Info className="h-4 w-4" />
                  Como funciona
                </h4>
                <p className="text-sm text-blue-700 mt-1">
                  Quando visitantes conectarem ao WiFi, serão redirecionados para uma página de cadastro/login.
                  Após o cadastro, o acesso à internet é liberado automaticamente.
                </p>
                <p className="text-sm text-blue-700 mt-2">
                  <strong>URL do Portal:</strong>{" "}
                  <code className="bg-blue-100 px-2 py-0.5 rounded">
                    {process.env.REACT_APP_BACKEND_URL}/api/wifi/cp
                  </code>
                </p>
              </div>

              {/* TP-Link Setup Instructions */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h4 className="font-medium text-amber-800 flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  Configuração no TP-Link EAP245
                </h4>
                <ol className="text-sm text-amber-700 mt-2 list-decimal list-inside space-y-1">
                  <li>Acesse o Omada Controller ou a interface do AP</li>
                  <li>Vá em Wireless → Portal</li>
                  <li>Habilite o Portal Authentication</li>
                  <li>Selecione "External Web Portal"</li>
                  <li>Cole a URL do Portal acima no campo "External Web Portal URL"</li>
                  <li>Configure o SSID desejado para usar o portal</li>
                </ol>
              </div>

              {/* Portal URL Display */}
              <div className="space-y-2">
                <Label>URL do Portal para configurar no roteador</Label>
                <div className="flex gap-2">
                  <Input 
                    readOnly
                    value={`${process.env.REACT_APP_BACKEND_URL}/api/wifi/cp`}
                    className="font-mono text-sm"
                  />
                  <Button
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/wifi/cp`);
                      toast.success("URL copiada!");
                    }}
                  >
                    Copiar
                  </Button>
                </div>
              </div>

              <Separator />

              {/* Preview Link */}
              <div className="flex justify-between items-center">
                <div>
                  <h4 className="font-medium">Visualizar Portal</h4>
                  <p className="text-sm text-muted-foreground">Veja como o portal aparece para os visitantes</p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/wifi/cp`, '_blank')}
                >
                  Abrir Preview
                  <Link className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Omada Controller Integration Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Integração Omada Controller (Autorização Automática)
              </CardTitle>
              <CardDescription>
                Configure a integração com o Omada Controller para liberar o acesso à internet automaticamente após o cadastro
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <h4 className="font-medium text-green-800 flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" />
                  Benefício da Integração
                </h4>
                <p className="text-sm text-green-700 mt-1">
                  Com a integração configurada, quando o visitante se cadastrar no portal, 
                  o sistema enviará automaticamente o comando de liberação para o Omada Controller,
                  permitindo acesso imediato à internet sem intervenção manual.
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>URL do Omada Controller</Label>
                  <Input 
                    placeholder="https://192.168.0.1:8043 ou https://omada.tplinkcloud.com"
                    className="font-mono text-sm"
                    value={omadaConfig.url}
                    onChange={(e) => setOmadaConfig(prev => ({ ...prev, url: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground">
                    URL de acesso ao seu Omada Controller (local ou cloud)
                  </p>
                </div>

                <Separator />

                <div className="space-y-4">
                  <h4 className="font-medium">Método de Autenticação</h4>
                  
                  <div className="p-4 border rounded-lg space-y-4">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">Recomendado</Badge>
                      <span className="font-medium">Hotspot API (Operador Portal)</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Use as credenciais de um operador do Portal no Omada Controller.
                      Crie um operador em: Omada → Configurações → Portal → Gerenciamento de Conta de Operador
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Usuário do Operador</Label>
                        <Input 
                          placeholder="admin_portal"
                          value={omadaConfig.operator_username}
                          onChange={(e) => setOmadaConfig(prev => ({ ...prev, operator_username: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Senha do Operador</Label>
                        <Input 
                          type="password" 
                          placeholder="••••••••"
                          value={omadaConfig.operator_password}
                          onChange={(e) => setOmadaConfig(prev => ({ ...prev, operator_password: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="p-4 border rounded-lg space-y-4 opacity-75">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">Avançado</Badge>
                      <span className="font-medium">OpenAPI (Omada 5.0+)</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Para Omada Controller 5.0 ou superior. Requer configuração de API Client.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Client ID</Label>
                        <Input 
                          placeholder="omada_api_client" 
                          disabled 
                          value={omadaConfig.client_id}
                          onChange={(e) => setOmadaConfig(prev => ({ ...prev, client_id: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Client Secret</Label>
                        <Input 
                          type="password" 
                          placeholder="••••••••" 
                          disabled
                          value={omadaConfig.client_secret}
                          onChange={(e) => setOmadaConfig(prev => ({ ...prev, client_secret: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Controller ID</Label>
                        <Input 
                          placeholder="abc123..." 
                          disabled
                          value={omadaConfig.controller_id}
                          onChange={(e) => setOmadaConfig(prev => ({ ...prev, controller_id: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Site ID</Label>
                        <Input 
                          placeholder="Default" 
                          disabled
                          value={omadaConfig.site_id}
                          onChange={(e) => setOmadaConfig(prev => ({ ...prev, site_id: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Test Result */}
                {omadaTestResult && (
                  <div className={`p-4 rounded-lg ${omadaTestResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                    <div className="flex items-center gap-2">
                      {omadaTestResult.success ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      ) : (
                        <X className="h-5 w-5 text-red-600" />
                      )}
                      <span className={omadaTestResult.success ? 'text-green-800 font-medium' : 'text-red-800 font-medium'}>
                        {omadaTestResult.success ? 'Conexão OK!' : 'Falha na conexão'}
                      </span>
                    </div>
                    <p className={`text-sm mt-1 ${omadaTestResult.success ? 'text-green-700' : 'text-red-700'}`}>
                      {omadaTestResult.message}
                    </p>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={testOmadaConnection}
                    disabled={testingOmada}
                  >
                    {testingOmada ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Testando...
                      </>
                    ) : (
                      'Testar Conexão'
                    )}
                  </Button>
                  <Button 
                    className="flex-1"
                    onClick={saveOmadaConfig}
                    disabled={savingOmada}
                  >
                    {savingOmada ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Salvando...
                      </>
                    ) : (
                      'Salvar Configuração'
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== TOOLS TAB (Admin Only) ===== */}
        <TabsContent value="tools" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Ferramentas de Manutenção
              </CardTitle>
              <CardDescription>
                Ferramentas administrativas para correção e manutenção de dados
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Fix Audição Status */}
              <div className="p-4 border rounded-lg space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <RefreshCw className="h-5 w-5 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium">Corrigir Status de Audição</h4>
                    <p className="text-sm text-muted-foreground">
                      Move pessoas com trilho completo de "Em Audição" para "Aguardando Ativação".
                      Use quando alguém foi aprovado na audição mas o sistema não atualizou automaticamente.
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={async () => {
                    if (!window.confirm("Deseja corrigir todas as pessoas presas em 'Em Audição' que já completaram o trilho?")) return;
                    try {
                      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/ministries/admin/fix-audicao-status`);
                      if (res.data.fixed_count > 0) {
                        toast.success(`${res.data.fixed_count} pessoa(s) corrigida(s)!`);
                        if (res.data.details?.length > 0) {
                          const names = res.data.details.map(d => d.pessoa).join(", ");
                          toast.info(`Corrigidos: ${names}`);
                        }
                      } else {
                        toast.info("Nenhuma pessoa precisou de correção.");
                      }
                    } catch (error) {
                      toast.error(error.response?.data?.detail || "Erro ao executar correção");
                    }
                  }}
                  data-testid="fix-audicao-btn"
                >
                  Executar Correção de Audições
                </Button>
              </div>

              {/* Sync Functions */}
              <div className="p-4 border rounded-lg space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium">Sincronizar Funções</h4>
                    <p className="text-sm text-muted-foreground">
                      Sincroniza funções habilitadas dos membros com base nas funções desejadas e ministérios.
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={async () => {
                    if (!window.confirm("Deseja sincronizar as funções de todos os membros?")) return;
                    try {
                      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/ministries/sync-member-functions`);
                      toast.success(res.data.message);
                    } catch (error) {
                      toast.error(error.response?.data?.detail || "Erro ao sincronizar funções");
                    }
                  }}
                  data-testid="sync-functions-btn"
                >
                  Sincronizar Funções
                </Button>
              </div>

              {/* Detect Duplicates */}
              <div className="p-4 border rounded-lg space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Users className="h-5 w-5 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium">Detectar Cadastros Duplicados</h4>
                    <p className="text-sm text-muted-foreground">
                      Encontra pessoas com mesmo telefone, email ou nome para mesclagem.
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={async () => {
                    try {
                      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/ministries/admin/duplicates`);
                      const { total_pessoas, duplicatas_encontradas, duplicatas } = res.data;
                      
                      if (duplicatas_encontradas === 0) {
                        toast.success(`Nenhuma duplicata encontrada entre ${total_pessoas} pessoas!`);
                        return;
                      }
                      
                      // Show duplicates in a more detailed way
                      let message = `Encontradas ${duplicatas_encontradas} possíveis duplicatas:\n\n`;
                      duplicatas.slice(0, 5).forEach((d, i) => {
                        message += `${i+1}. ${d.pessoa_1.nome} <-> ${d.pessoa_2.nome} (${d.match_type}: ${d.match_value})\n`;
                      });
                      if (duplicatas_encontradas > 5) {
                        message += `\n... e mais ${duplicatas_encontradas - 5} duplicata(s)`;
                      }
                      message += "\n\nDeseja ver a lista completa no console?";
                      
                      if (window.confirm(message)) {
                        console.log("=== DUPLICATAS ENCONTRADAS ===");
                        console.table(duplicatas.map(d => ({
                          pessoa1: d.pessoa_1.nome,
                          pessoa1_id: d.pessoa_1.id,
                          pessoa2: d.pessoa_2.nome,
                          pessoa2_id: d.pessoa_2.id,
                          tipo: d.match_type,
                          valor: d.match_value
                        })));
                        toast.info("Lista de duplicatas exibida no console do navegador (F12)");
                      }
                    } catch (error) {
                      toast.error(error.response?.data?.detail || "Erro ao buscar duplicatas");
                    }
                  }}
                  data-testid="detect-duplicates-btn"
                >
                  Buscar Duplicatas
                </Button>
              </div>

              {/* Info Card */}
              <div className="p-4 border rounded-lg bg-blue-50 dark:bg-blue-950/20">
                <div className="flex gap-3">
                  <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium text-blue-800 dark:text-blue-200 mb-1">Dica</p>
                    <p>
                      Estas ferramentas são seguras e podem ser executadas a qualquer momento.
                      Elas apenas corrigem dados inconsistentes, não apagam nenhuma informação.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== FORMS TAB ===== */}
        <TabsContent value="forms" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                Personalização Visual
              </CardTitle>
              <CardDescription>
                Configure as cores e aparência dos formulários
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Cor Primária</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={formConfig.cor_primaria}
                      onChange={(e) => setFormConfig({ ...formConfig, cor_primaria: e.target.value })}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={formConfig.cor_primaria}
                      onChange={(e) => setFormConfig({ ...formConfig, cor_primaria: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Cor Secundária</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={formConfig.cor_secundaria}
                      onChange={(e) => setFormConfig({ ...formConfig, cor_secundaria: e.target.value })}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={formConfig.cor_secundaria}
                      onChange={(e) => setFormConfig({ ...formConfig, cor_secundaria: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Cor do Texto</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={formConfig.cor_texto}
                      onChange={(e) => setFormConfig({ ...formConfig, cor_texto: e.target.value })}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={formConfig.cor_texto}
                      onChange={(e) => setFormConfig({ ...formConfig, cor_texto: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Cor de Fundo</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={formConfig.cor_fundo}
                      onChange={(e) => setFormConfig({ ...formConfig, cor_fundo: e.target.value })}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={formConfig.cor_fundo}
                      onChange={(e) => setFormConfig({ ...formConfig, cor_fundo: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>URL do Logo (opcional)</Label>
                <Input
                  value={formConfig.logo_url}
                  onChange={(e) => setFormConfig({ ...formConfig, logo_url: e.target.value })}
                  placeholder="https://exemplo.com/logo.png"
                />
              </div>

              {/* Preview das cores */}
              <div className="p-4 rounded-lg border" style={{ backgroundColor: formConfig.cor_fundo }}>
                <p className="text-sm mb-2" style={{ color: formConfig.cor_texto }}>Preview das cores:</p>
                <div className="flex gap-2">
                  <Button size="sm" style={{ backgroundColor: formConfig.cor_primaria, color: '#fff' }}>
                    Botão Primário
                  </Button>
                  <Button size="sm" style={{ backgroundColor: formConfig.cor_secundaria, color: '#fff' }}>
                    Botão Secundário
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Portal WiFi Config */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wifi className="h-5 w-5" />
                Portal Cativo (WiFi)
              </CardTitle>
              <CardDescription>
                Configure os textos e campos do portal de login WiFi
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Título do Portal</Label>
                  <Input
                    value={formConfig.titulo_portal_wifi}
                    onChange={(e) => setFormConfig({ ...formConfig, titulo_portal_wifi: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Subtítulo</Label>
                  <Input
                    value={formConfig.subtitulo_portal_wifi}
                    onChange={(e) => setFormConfig({ ...formConfig, subtitulo_portal_wifi: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formConfig.portal_wifi.mostrar_login}
                    onCheckedChange={(checked) => setFormConfig(prev => ({
                      ...prev,
                      portal_wifi: { ...prev.portal_wifi, mostrar_login: checked }
                    }))}
                  />
                  <Label>Mostrar Login (já cadastrado)</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formConfig.portal_wifi.mostrar_cadastro}
                    onCheckedChange={(checked) => setFormConfig(prev => ({
                      ...prev,
                      portal_wifi: { ...prev.portal_wifi, mostrar_cadastro: checked }
                    }))}
                  />
                  <Label>Mostrar Cadastro</Label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cadastro Público Config */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Cadastro Público
              </CardTitle>
              <CardDescription>
                Configure os textos e campos do formulário de cadastro público
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Título</Label>
                  <Input
                    value={formConfig.titulo_cadastro_publico}
                    onChange={(e) => setFormConfig({ ...formConfig, titulo_cadastro_publico: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Subtítulo</Label>
                  <Input
                    value={formConfig.subtitulo_cadastro_publico}
                    onChange={(e) => setFormConfig({ ...formConfig, subtitulo_cadastro_publico: e.target.value })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Campos do Formulário */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Campos do Formulário
              </CardTitle>
              <CardDescription>
                Configure quais campos aparecem nos formulários e se são obrigatórios
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid gap-3">
                  {Object.entries(formConfig.campos).map(([campo, config]) => (
                    <div key={campo} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50">
                      <div className="flex items-center gap-4 flex-1">
                        <div className="flex items-center gap-2 min-w-[180px]">
                          <Switch
                            checked={config.visivel}
                            onCheckedChange={() => toggleCampoVisivel(campo)}
                          />
                          <span className={`text-sm ${!config.visivel ? 'text-muted-foreground' : ''}`}>
                            {config.label}
                          </span>
                        </div>
                        <Input
                          value={config.label}
                          onChange={(e) => updateCampoLabel(campo, e.target.value)}
                          className="max-w-[200px] h-8"
                          placeholder="Nome do campo"
                          disabled={!config.visivel}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={config.obrigatorio}
                          onCheckedChange={() => toggleCampoObrigatorio(campo)}
                          disabled={!config.visivel || campo === 'nome_completo'}
                        />
                        <Badge variant={config.obrigatorio ? "default" : "outline"} className="min-w-[90px] justify-center">
                          {config.obrigatorio ? "Obrigatório" : "Opcional"}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button onClick={saveFormConfig} disabled={savingFormConfig}>
              {savingFormConfig ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Salvar Configurações
                </>
              )}
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      {/* ===== PROFILE DIALOG ===== */}
      <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{editingProfile ? "Editar Perfil" : "Novo Perfil"}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh] pr-4">
            <form onSubmit={handleProfileSubmit} className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nome do Perfil *</Label>
                <Input
                  required
                  value={profileForm.nome}
                  onChange={(e) => setProfileForm({ ...profileForm, nome: e.target.value })}
                  placeholder="Ex: Líder de Ministério"
                  data-testid="profile-nome"
                />
              </div>

              <div className="space-y-2">
                <Label>Descrição</Label>
                <Input
                  value={profileForm.descricao}
                  onChange={(e) => setProfileForm({ ...profileForm, descricao: e.target.value })}
                  placeholder="Descrição do perfil"
                  data-testid="profile-descricao"
                />
              </div>

              <Separator />

              <div className="space-y-4">
                <h3 className="font-medium">Permissões por Módulo</h3>
                
                <div className="space-y-3">
                  {mainModules.map((module) => (
                    <div key={module}>
                      <div className="p-3 rounded-lg bg-muted/50">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm">{moduleLabels[module]}</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {Object.keys(actionLabels).map((action) => {
                            const isChecked = profileForm.permissoes[module]?.[action] || false;
                            return (
                              <Badge
                                key={`${module}-${action}`}
                                variant={isChecked ? "default" : "outline"}
                                className="cursor-pointer gap-1"
                                onClick={() => togglePermission(module, action)}
                              >
                                {isChecked ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                                {actionLabels[action]}
                              </Badge>
                            );
                          })}
                        </div>
                      </div>
                      
                      {/* Sub-módulos de Configurações */}
                      {module === "configuracoes" && (
                        <div className="ml-4 mt-2 space-y-2 border-l-2 border-orange-300 pl-4">
                          {configSubModules.map((subModule) => (
                            <div key={subModule} className="p-3 rounded-lg bg-orange-50 dark:bg-orange-950/20">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium text-sm text-orange-700 dark:text-orange-300">
                                  ↳ {moduleLabels[subModule]}
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {Object.keys(actionLabels).map((action) => {
                                  const isChecked = profileForm.permissoes[subModule]?.[action] || false;
                                  return (
                                    <Badge
                                      key={`${subModule}-${action}`}
                                      variant={isChecked ? "default" : "outline"}
                                      className="cursor-pointer gap-1"
                                      onClick={() => togglePermission(subModule, action)}
                                    >
                                      {isChecked ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                                      {actionLabels[action]}
                                    </Badge>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-sm">Criar usuário a partir de pessoa</span>
                      <p className="text-xs text-muted-foreground">Permite criar login do sistema para uma pessoa cadastrada</p>
                    </div>
                    <Switch
                      checked={profileForm.criar_usuario_pessoa}
                      onCheckedChange={(checked) => setProfileForm({ ...profileForm, criar_usuario_pessoa: checked })}
                      data-testid="profile-criar-usuario"
                    />
                  </div>
                </div>

                <div className="p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-sm">Aprovar como Membro</span>
                      <p className="text-xs text-muted-foreground">Permite marcar uma pessoa como "Aprovado como Membro"</p>
                    </div>
                    <Switch
                      checked={profileForm.aprovar_membro}
                      onCheckedChange={(checked) => setProfileForm({ ...profileForm, aprovar_membro: checked })}
                      data-testid="profile-aprovar-membro"
                    />
                  </div>
                </div>

                <div className="p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-sm">Aprovar na Função</span>
                      <p className="text-xs text-muted-foreground">Permite marcar uma pessoa como "Aprovado na Função" (voluntário)</p>
                    </div>
                    <Switch
                      checked={profileForm.aprovar_funcao}
                      onCheckedChange={(checked) => setProfileForm({ ...profileForm, aprovar_funcao: checked })}
                      data-testid="profile-aprovar-funcao"
                    />
                  </div>
                </div>

                <div className="p-3 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-sm text-green-800 dark:text-green-200">Perfil para Voluntário Aprovado</span>
                      <p className="text-xs text-green-700 dark:text-green-300">Este perfil será atribuído automaticamente ao aprovar um voluntário</p>
                    </div>
                    <Switch
                      checked={profileForm.perfil_voluntario_aprovado}
                      onCheckedChange={(checked) => setProfileForm({ ...profileForm, perfil_voluntario_aprovado: checked })}
                      data-testid="profile-voluntario-aprovado"
                    />
                  </div>
                </div>

                <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-sm text-blue-800 dark:text-blue-200">Ver Todas as Escalas no Início</span>
                      <p className="text-xs text-blue-700 dark:text-blue-300">Permite visualizar todas as escalas na tela de Início, mesmo não estando escalado</p>
                    </div>
                    <Switch
                      checked={profileForm.ver_todas_escalas}
                      onCheckedChange={(checked) => setProfileForm({ ...profileForm, ver_todas_escalas: checked })}
                      data-testid="profile-ver-todas-escalas"
                    />
                  </div>
                </div>

                <Separator className="my-4" />
                
                <div className="space-y-3">
                  <h3 className="font-medium">Permissões "Ver Todos"</h3>
                  <p className="text-sm text-muted-foreground">Permite visualizar todos os itens, independente de ser líder. Sem essas permissões, o usuário só verá os itens que lidera.</p>
                  
                  <div className="p-3 rounded-lg bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-900">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium text-sm text-purple-800 dark:text-purple-200">Ver Todas as Pessoas</span>
                        <p className="text-xs text-purple-700 dark:text-purple-300">Ver todas as pessoas cadastradas, não apenas dos ministérios/projetos que lidera</p>
                      </div>
                      <Switch
                        checked={profileForm.ver_todas_pessoas}
                        onCheckedChange={(checked) => setProfileForm({ ...profileForm, ver_todas_pessoas: checked })}
                        data-testid="profile-ver-todas-pessoas"
                      />
                    </div>
                  </div>
                  
                  <div className="p-3 rounded-lg bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-900">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium text-sm text-purple-800 dark:text-purple-200">Ver Todos os Ministérios</span>
                        <p className="text-xs text-purple-700 dark:text-purple-300">Ver todos os ministérios, não apenas os que lidera</p>
                      </div>
                      <Switch
                        checked={profileForm.ver_todos_ministerios}
                        onCheckedChange={(checked) => setProfileForm({ ...profileForm, ver_todos_ministerios: checked })}
                        data-testid="profile-ver-todos-ministerios"
                      />
                    </div>
                  </div>
                  
                  <div className="p-3 rounded-lg bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-900">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium text-sm text-purple-800 dark:text-purple-200">Ver Todos os Projetos</span>
                        <p className="text-xs text-purple-700 dark:text-purple-300">Ver todos os projetos, não apenas os que lidera</p>
                      </div>
                      <Switch
                        checked={profileForm.ver_todos_projetos}
                        onCheckedChange={(checked) => setProfileForm({ ...profileForm, ver_todos_projetos: checked })}
                        data-testid="profile-ver-todos-projetos"
                      />
                    </div>
                  </div>
                </div>

                <Separator className="my-4" />

                {/* WhatsApp Notifications */}
                <div className="space-y-3">
                  <h3 className="font-medium flex items-center gap-2">
                    <MessageCircle className="h-4 w-4 text-green-600" />
                    Notificações WhatsApp
                  </h3>
                  <p className="text-sm text-muted-foreground">Configure quem recebe notificações de novas mensagens do WhatsApp</p>
                  
                  <div className="p-3 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium text-sm text-green-800 dark:text-green-200">Receber Notificações de Mensagens</span>
                        <p className="text-xs text-green-700 dark:text-green-300">Receber push notification quando chegar nova mensagem no WhatsApp</p>
                      </div>
                      <Switch
                        checked={profileForm.receber_notificacoes_whatsapp}
                        onCheckedChange={(checked) => setProfileForm({ ...profileForm, receber_notificacoes_whatsapp: checked })}
                        data-testid="profile-notificacoes-whatsapp"
                      />
                    </div>
                  </div>
                </div>

                <Separator className="my-4" />

                <div className="space-y-3">
                  <h3 className="font-medium">Visibilidade dos Cards do Dashboard</h3>
                  <p className="text-sm text-muted-foreground">Defina quais cards serão exibidos no Dashboard para este perfil</p>
                  
                  {Object.entries(dashboardCardLabels).map(([cardKey, cardLabel]) => (
                    <div key={cardKey} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <span className="text-sm">{cardLabel}</span>
                      <Switch
                        checked={profileForm.permissoes?.dashboard_cards?.[cardKey] ?? true}
                        onCheckedChange={(checked) => {
                          setProfileForm({
                            ...profileForm,
                            permissoes: {
                              ...profileForm.permissoes,
                              dashboard_cards: {
                                ...(profileForm.permissoes?.dashboard_cards || {}),
                                [cardKey]: checked
                              }
                            }
                          });
                        }}
                        data-testid={`dashboard-card-${cardKey}`}
                      />
                    </div>
                  ))}
                </div>

                <Separator className="my-4" />

                {/* Atalho na Navegação Inferior */}
                <div className="space-y-3">
                  <h3 className="font-medium flex items-center gap-2">
                    <Zap className="h-4 w-4 text-yellow-600" />
                    Atalho na Navegação
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Configure um atalho personalizado que aparecerá na barra de navegação inferior (substituirá "Eventos")
                  </p>
                  
                  <div className="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-900">
                    <Label className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Selecione o Atalho</Label>
                    <Select
                      value={profileForm.atalho || "none"}
                      onValueChange={(value) => setProfileForm({ ...profileForm, atalho: value === "none" ? "" : value })}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Nenhum atalho (Eventos padrão)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Nenhum (Eventos padrão)</SelectItem>
                        <SelectItem value="ministerios">Ministérios</SelectItem>
                        <SelectItem value="pessoas">Pessoas</SelectItem>
                        <SelectItem value="projetos">Projetos</SelectItem>
                        <SelectItem value="musicas">Músicas</SelectItem>
                        <SelectItem value="trilho">Trilho do Reino</SelectItem>
                        <SelectItem value="whatsapp">WhatsApp</SelectItem>
                        <SelectItem value="devocional">Devocional</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-2">
                      Este atalho aparecerá na barra inferior no lugar de "Eventos"
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setProfileDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1" data-testid="save-profile-button">
                  Salvar
                </Button>
              </div>
            </form>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* ===== BRANCH DIALOG ===== */}
      <Dialog open={branchDialogOpen} onOpenChange={setBranchDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingBranch ? "Editar Unidade" : "Nova Unidade"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBranchSubmit} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input
                required
                value={branchForm.nome}
                onChange={(e) => setBranchForm({ ...branchForm, nome: e.target.value })}
                placeholder="Ex: Sede Principal"
                data-testid="branch-nome"
              />
            </div>

            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select
                value={branchForm.tipo}
                onValueChange={(val) => setBranchForm({ ...branchForm, tipo: val })}
              >
                <SelectTrigger data-testid="branch-tipo">
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="igreja">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      Igreja
                    </div>
                  </SelectItem>
                  <SelectItem value="instituto_social">
                    <div className="flex items-center gap-2">
                      <Heart className="h-4 w-4 text-pink-500" />
                      Instituto Social
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {branchForm.tipo === "instituto_social" 
                  ? "Organização focada em projetos sociais e comunitários" 
                  : "Congregação religiosa com atividades de culto e louvor"}
              </p>
            </div>

            <div className="space-y-2">
              <Label>Endereço</Label>
              <Input
                value={branchForm.endereco}
                onChange={(e) => setBranchForm({ ...branchForm, endereco: e.target.value })}
                placeholder="Rua, número, bairro, cidade"
                data-testid="branch-endereco"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Telefone</Label>
                <Input
                  value={branchForm.telefone}
                  onChange={(e) => setBranchForm({ ...branchForm, telefone: e.target.value })}
                  placeholder="(00) 00000-0000"
                  data-testid="branch-telefone"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={branchForm.email}
                  onChange={(e) => setBranchForm({ ...branchForm, email: e.target.value })}
                  placeholder="email@igreja.com"
                  data-testid="branch-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Sobre Nós</Label>
              <Textarea
                value={branchForm.sobre_nos}
                onChange={(e) => setBranchForm({ ...branchForm, sobre_nos: e.target.value })}
                placeholder="Texto que aparecerá na página News (ex: Venha fazer parte da nossa família)"
                rows={3}
                data-testid="branch-sobre-nos"
              />
              <p className="text-xs text-muted-foreground">Este texto aparece abaixo do nome na página News</p>
            </div>

            {/* Localização para Check-in Automático */}
            <Separator />
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" />
                <Label className="text-base font-medium">Check-in por Localização</Label>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Latitude</Label>
                  <Input
                    type="number"
                    step="any"
                    value={branchForm.latitude}
                    onChange={(e) => setBranchForm({ ...branchForm, latitude: e.target.value })}
                    placeholder="-23.550520"
                    data-testid="branch-latitude"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Longitude</Label>
                  <Input
                    type="number"
                    step="any"
                    value={branchForm.longitude}
                    onChange={(e) => setBranchForm({ ...branchForm, longitude: e.target.value })}
                    placeholder="-46.633309"
                    data-testid="branch-longitude"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Dica: Abra o Google Maps, clique com botão direito no local e copie as coordenadas
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Raio do Check-in</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="50"
                      max="500"
                      value={branchForm.raio_checkin_metros}
                      onChange={(e) => setBranchForm({ ...branchForm, raio_checkin_metros: parseInt(e.target.value) || 100 })}
                      className="w-24"
                      data-testid="branch-raio"
                    />
                    <span className="text-sm text-muted-foreground">metros</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Tempo mínimo</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="5"
                      max="60"
                      value={branchForm.tempo_minimo_checkin_minutos}
                      onChange={(e) => setBranchForm({ ...branchForm, tempo_minimo_checkin_minutos: parseInt(e.target.value) || 20 })}
                      className="w-24"
                      data-testid="branch-tempo-minimo"
                    />
                    <span className="text-sm text-muted-foreground">minutos</span>
                  </div>
                </div>
              </div>

              {/* Toggles de Check-in */}
              <div className="space-y-3 pt-2">
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-green-600" />
                    <div>
                      <span className="font-medium text-sm">Check-in via GPS</span>
                      <p className="text-xs text-muted-foreground">Registra presença automaticamente quando a pessoa está nas proximidades</p>
                    </div>
                  </div>
                  <Switch
                    checked={branchForm.permite_checkin_gps}
                    onCheckedChange={(checked) => setBranchForm({ ...branchForm, permite_checkin_gps: checked })}
                    data-testid="branch-checkin-gps"
                  />
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                  <div className="flex items-center gap-2">
                    <Wifi className="h-4 w-4 text-blue-600" />
                    <div>
                      <span className="font-medium text-sm">Check-in via WiFi</span>
                      <p className="text-xs text-muted-foreground">Registra presença quando a pessoa conecta ao WiFi da unidade</p>
                    </div>
                  </div>
                  <Switch
                    checked={branchForm.permite_checkin_wifi}
                    onCheckedChange={(checked) => setBranchForm({ ...branchForm, permite_checkin_wifi: checked })}
                    data-testid="branch-checkin-wifi"
                  />
                </div>
              </div>
            </div>

            {/* Termos de Uso */}
            <Separator />
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                <Label className="text-base font-medium">Termos de Uso</Label>
              </div>
              
              <Textarea
                value={branchForm.termos_uso}
                onChange={(e) => setBranchForm({ ...branchForm, termos_uso: e.target.value })}
                placeholder="Digite os termos de uso da unidade..."
                rows={8}
                className="font-mono text-sm"
                data-testid="branch-termos"
              />
              <p className="text-xs text-muted-foreground">
                Estes termos serão exibidos para os usuários no primeiro acesso. Inclua autorizações de imagem e uso de localização.
              </p>
            </div>

            <Separator />

            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <div>
                <span className="font-medium text-sm">Ativa</span>
                <p className="text-xs text-muted-foreground">Unidades inativas não aparecem para seleção</p>
              </div>
              <Switch
                checked={branchForm.ativa}
                onCheckedChange={(checked) => setBranchForm({ ...branchForm, ativa: checked })}
                data-testid="branch-ativa"
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setBranchDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="flex-1" data-testid="save-branch-button">
                Salvar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
